import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Info, Calculator, CheckCircle2 } from 'lucide-react';
export function PricingCalculator() {
  const [systemType, setSystemType] = useState<'split' | 'multi' | 'ducted'>('ducted');
  const [size, setSize] = useState<'small' | 'medium' | 'large' | 'xlarge'>('medium');
  const homeSizes = {
    small: {
      label: '1 to 2 bed',
      sqm: '60 to 110 sqm'
    },
    medium: {
      label: '3 bedroom',
      sqm: '120 to 180 sqm'
    },
    large: {
      label: '4 bedroom',
      sqm: '180 to 260 sqm'
    },
    xlarge: {
      label: '5+ bedroom',
      sqm: '260+ sqm'
    }
  };
  const systems = {
    split: {
      label: 'Single split',
      desc: 'One room'
    },
    multi: {
      label: 'Multi split',
      desc: '2 to 4 rooms'
    },
    ducted: {
      label: 'Ducted',
      desc: 'Whole home'
    }
  };
  const estimate = useMemo(() => {
    const ranges: Record<string, Record<string, [number, number]>> = {
      split: {
        small: [2500, 3800],
        medium: [3200, 4800],
        large: [4500, 6500],
        xlarge: [5500, 8000]
      },
      multi: {
        small: [5500, 8500],
        medium: [7500, 12000],
        large: [10500, 15500],
        xlarge: [13000, 18000]
      },
      ducted: {
        small: [10000, 13000],
        medium: [13000, 16500],
        large: [16500, 20000],
        xlarge: [18000, 24000]
      }
    };
    return ranges[systemType][size];
  }, [systemType, size]);
  const monthlyFinance = Math.round(estimate[0] / 60 / 5) * 5;
  return <section id="pricing" className="bg-white py-20 md:py-28 scroll-mt-24" data-id="element-454" data-mp="2ea8o9te55b43">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-455" data-mp="2jawggie3vxzw">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16" data-id="element-456" data-mp="2j80gypmm119c">
          {/* Left column */}
          <div className="lg:col-span-5" data-id="element-457" data-mp="3psjjkmm40to6">
            <div className="text-xs font-semibold uppercase tracking-widest text-brand mb-4" data-id="element-458" data-mp="3uoqsmo9sylac">
              Real Perth Prices
            </div>
            <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-ink leading-tight mb-6" data-id="element-459" data-mp="1ulfvhxsku5tb">
              How much will it actually cost?
            </h2>
            <p className="text-lg text-stone-600 leading-relaxed mb-8 max-w-md" data-id="element-460" data-mp="23mxi7z0hp5qz">
              Most installers hide their numbers. We publish ours. Pick your
              system and home size for a realistic Perth installed range.
            </p>

            <div className="space-y-4 text-sm text-stone-700" data-id="element-461" data-mp="1buew58njo898">
              <div className="flex gap-3" data-id="element-462" data-mp="16yziohtg8edc">
                <CheckCircle2 size={18} className="text-success flex-shrink-0 mt-0.5" data-id="element-463" data-mp="1kp30mwlaplwz" />
                <p data-id="element-464" data-mp="1fxbklfzrmyl3">
                  <strong className="text-ink" data-id="element-465" data-mp="1fwthq0illm47">What is included:</strong> Unit,
                  indoor and outdoor install, all electrical, mounting hardware,
                  removal and disposal of old gear, commissioning and
                  walkthrough.
                </p>
              </div>
              <div className="flex gap-3" data-id="element-466" data-mp="e1p3g2utxdqf">
                <CheckCircle2 size={18} className="text-success flex-shrink-0 mt-0.5" data-id="element-467" data-mp="itq9h71em880" />
                <p data-id="element-468" data-mp="46eyp7d1qs8y">
                  <strong className="text-ink" data-id="element-469" data-mp="46x4ovpigku2">Fixed price promise:</strong>{' '}
                  Once your on-site quote is issued, that is the price. No variations on install day.
                </p>
              </div>
              <div className="flex gap-3" data-id="element-470" data-mp="wrx145frfbz8">
                <CheckCircle2 size={18} className="text-success flex-shrink-0 mt-0.5" data-id="element-471" data-mp="11rt4q3kjp1e9" />
                <p data-id="element-472" data-mp="mxo9rl2gtp2n">
                  <strong className="text-ink" data-id="element-473" data-mp="ruog7x388tua">Finance available:</strong>{' '}
                  Interest free options through Brighte and humm for jobs over
                  $5,000.
                </p>
              </div>
            </div>

            <div className="mt-8 p-4 bg-brand-50 rounded-2xl border border-brand/10" data-id="element-474" data-mp="3clymvryv1kh5">
              <p className="text-sm text-ink leading-relaxed" data-id="element-475" data-mp="3ssa7oloo9ln0">
                <strong data-id="element-476" data-mp="3stv64me3xoq5">Why assessment matters:</strong> An over-sized or under-sized system can cost more to run and wear out earlier. Every recommendation starts on site.
              </p>
            </div>
          </div>

          {/* Right column: calculator */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-7" data-id="element-477" data-mp="3e4p64w7g3uh7">
            <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-10 border border-stone-200 shadow-xl" data-id="element-478" data-mp="n7s2yiq7973">
              <div className="flex items-center gap-2 mb-7" data-id="element-479" data-mp="2qecq0y21qrfj">
                <Calculator size={18} className="text-brand" data-id="element-480" data-mp="2leqauj2nr143" />
                <span className="text-xs font-semibold uppercase tracking-widest text-stone-700" data-id="element-481" data-mp="2ld5kl5ndm4c1">
                  Cost Estimator
                </span>
              </div>

              {/* System type */}
              <div className="mb-7" data-id="element-482" data-mp="15tg2feoyj1zu">
                <label className="block text-sm font-semibold text-ink mb-3" data-id="element-483" data-mp="1au3orn28qunq">
                  What kind of system?
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-484" data-mp="1fmwxem0tyn53">
                  {Object.entries(systems).map(([key, sys]) => <button key={key} onClick={() => setSystemType(key as 'split' | 'multi' | 'ducted')} className={`text-left p-3 rounded-xl border-2 transition-all ${systemType === key ? 'bg-brand text-white border-brand' : 'bg-white border-stone-200 hover:border-brand/40 text-ink'}`} data-id="element-485" data-mp="1kmrjmc9nuojt">
                      <div className="font-semibold text-sm leading-tight" data-id="element-486" data-mp="45x71vy1midp">
                        {sys.label}
                      </div>
                      <div className={`text-xs mt-1 ${systemType === key ? 'text-white/80' : 'text-stone-500'}`} data-id="element-487" data-mp="3sbdipx5ms0zh">
                        {sys.desc}
                      </div>
                    </button>)}
                </div>
              </div>

              {/* Home size */}
              <div className="mb-8" data-id="element-488" data-mp="f4br8hubmw35">
                <label className="block text-sm font-semibold text-ink mb-3" data-id="element-489" data-mp="f3a4vh7uun64">
                  Home size?
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-490" data-mp="b2hvmnyzgi9u">
                  {Object.entries(homeSizes).map(([key, hs]) => <button key={key} onClick={() => setSize(key as 'small' | 'medium' | 'large' | 'xlarge')} className={`p-3 rounded-xl border-2 transition-all text-left ${size === key ? 'bg-brand text-white border-brand' : 'bg-white border-stone-200 hover:border-brand/40 text-ink'}`} data-id="element-491" data-mp="jvbknd48ew0w">
                      <div className="font-semibold text-sm leading-tight" data-id="element-492" data-mp="1hflakqgwcfu0">
                        {hs.label}
                      </div>
                      <div className={`text-xs mt-1 ${size === key ? 'text-white/80' : 'text-stone-500'}`} data-id="element-493" data-mp="13iy3huv8c9ad">
                        {hs.sqm}
                      </div>
                    </button>)}
                </div>
              </div>

              {/* Result */}
              <motion.div key={`${systemType}-${size}`} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3
            }} className="bg-brand text-white rounded-2xl p-6 sm:p-7" data-id="element-494" data-mp="kqlz72l0mxxl">
                <div className="text-xs uppercase tracking-widest text-white/70 font-semibold mb-3" data-id="element-495" data-mp="v8qwu7whts1s">
                  Typical Range, Fully Installed
                </div>
                <div className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-none tabular-nums" data-id="element-496" data-mp="m7t1sf418uvc">
                  A${estimate[0].toLocaleString()}
                  <span className="text-white/50" data-id="element-497" data-mp="hbvpqkzsyaod"> to </span>
                  A${estimate[1].toLocaleString()}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/85" data-id="element-498" data-mp="7jg3vcldx1y7">
                  <span className="flex items-center gap-1.5" data-id="element-499" data-mp="3hbameyum7c3">
                    <Info size={13} className="text-accent" data-id="element-500" data-mp="3uoe63ilpyzs4" />
                    Or from ~${monthlyFinance}/month on interest free finance
                  </span>
                </div>

                <a href="#quote-form" className="mt-6 inline-flex items-center gap-2 px-6 h-12 rounded-xl bg-accent hover:bg-accent-600 text-ink text-sm font-bold transition-all hover:scale-[1.02] shadow-lg shadow-accent/20" data-id="element-501" data-mp="3pr5kt959yfmn">
                  Get my exact fixed quote
                  <ArrowRight size={15} strokeWidth={2.5} data-id="element-502" data-mp="3kkqp740yj3vw" />
                </a>
              </motion.div>

              <p className="mt-4 text-xs text-stone-500 leading-relaxed" data-id="element-503" data-mp="3pk3myjtd9vd9">
                Estimates based on real Perth installs over the past 12 months.
                Your exact quote depends on home layout, electrical access and
                brand chosen. Free on site visit, no obligation.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}